import type {
  WithdrawalFlowConfig,
  WithdrawalMethodOption,
} from '../../src/flow/withdrawalFlowConfig'
import { buildLighterFastWithdrawalConfig } from './lighterFastWithdrawal'
import { LIGHTER_FAST_METHOD_ID } from './lighterWithdrawalConfig'
import type { LighterWithdrawalFlowConfig } from './lighterWithdrawalConfig'
import { useLighterUnstakedFee } from './useLighterUnstakedFee'
import { useLighterWithdrawalMethods } from './useLighterWithdrawalMethods'

/**
 * The Lighter methods to hand `beginWithdrawal`, each carrying the config it
 * runs. Mirrors web's `useLighterWithdrawalConfig`: the host calls this and
 * passes the result on, so the SDK never holds Lighter config.
 *
 * Empty until the L2 account resolves — both rails address the account by
 * index, so there is nothing to offer before then, and the flow falls back to
 * its generic crypto row.
 */
export function useLighterWithdrawal({
  lighter,
  config,
}: {
  lighter: LighterWithdrawalFlowConfig | undefined
  /**
   * The host's own withdrawal config; title and callbacks carry over. Absent
   * until the host has one — a hook can't be called conditionally, so there is
   * no placeholder worth inventing.
   */
  config: WithdrawalFlowConfig | undefined
}): WithdrawalMethodOption[] {
  const { methods, accountIndex, isUnifiedTradingMode } =
    useLighterWithdrawalMethods(lighter)
  // Fast only; Secure charges no such fee. Keyed off the row actually being
  // offered, so the two account lookups stay off the wire when it isn't.
  const isFastOffered = methods.some(
    (method) => method.id === LIGHTER_FAST_METHOD_ID,
  )
  const { feeTokens: quoteInputFeeTokens, isFetched: quoteInputFeeSettled } =
    useLighterUnstakedFee({
      accountIndex,
      enabled: isFastOffered,
    })

  if (!lighter || !config || !accountIndex) {
    return []
  }

  // Native Bridge is filtered out: its screens have not shipped, and a row
  // that fails on tap is worse than no row.
  return methods
    .filter((method) => method.id === LIGHTER_FAST_METHOD_ID)
    .map((method) => ({
      id: method.id,
      title: method.title,
      subtitle: method.subtitle,
      config: {
        ...buildLighterFastWithdrawalConfig({
          config,
          lighter,
          accountIndex,
          isUnifiedTradingMode,
        }),
        // Lighter takes this off the amount before it is quoted.
        quoteInputFeeTokens,
        quoteInputFeeSettled,
      },
    }))
}
